import { motion } from "framer-motion";
import { AlertTriangle, Wrench, Users, TrendingUp, Hammer } from "lucide-react";
import { getRealm } from "@/components/lunimar/lunimarRealms";
import {
  ScenarioBrief,
  LunimarDecisionCard,
  ChoiceComparisonGrid,
  fmtMoney,
} from "@/components/lunimar-kit";

interface EventChoice {
  label: string;
  cash_impact: number;
  condition_impact: number;
  tenant_impact: number;
  learning: string;
}

interface Props {
  event: {
    id: string;
    title: string;
    description: string;
    choices: EventChoice[];
    category: string;
    target_property?: string;
  };
  onChoose: (choiceIndex: number) => void;
  processing: boolean;
}

const CATEGORY_ICON: Record<string, JSX.Element> = {
  repair: <Wrench className="h-4 w-4" />,
  tenant: <Users className="h-4 w-4" />,
  market: <TrendingUp className="h-4 w-4" />,
  renovation: <Hammer className="h-4 w-4" />,
};

function impactRisk(c: EventChoice): "Low" | "Medium" | "High" {
  const cashHit = c.cash_impact < -2000 || c.condition_impact < -10 || c.tenant_impact < -10;
  const minor = c.cash_impact >= -500 && c.condition_impact >= -3 && c.tenant_impact >= -3;
  if (cashHit) return "High";
  if (minor) return "Low";
  return "Medium";
}

function impactReward(c: EventChoice): "Low" | "Medium" | "High" {
  if (c.condition_impact >= 5 || c.tenant_impact >= 5 || c.cash_impact > 0) return "High";
  if (c.condition_impact >= 0 && c.tenant_impact >= 0) return "Medium";
  return "Low";
}

function tradeoffLine(c: EventChoice): string | undefined {
  const bits: string[] = [];
  if (c.condition_impact !== 0)
    bits.push(`${c.condition_impact > 0 ? "+" : ""}${c.condition_impact} condition`);
  if (c.tenant_impact !== 0)
    bits.push(`${c.tenant_impact > 0 ? "+" : ""}${c.tenant_impact} tenant quality`);
  return bits.length ? bits.join(" · ") : undefined;
}

export default function EventCard({ event, onChoose, processing }: Props) {
  const realm = getRealm("re_investing");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-lg max-h-[88vh] overflow-y-auto"
      >
        <div className="space-y-4">
          <ScenarioBrief
            realm={realm}
            eyebrow={`Live signal · ${event.category}`}
            title={event.title}
            intent={event.description}
          >
            {event.target_property && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] text-white/85">
                <AlertTriangle className="h-3 w-3" /> Affects: {event.target_property}
              </div>
            )}
          </ScenarioBrief>

          <div className="text-[10px] font-mono uppercase tracking-widest text-white/55">
            Decision required
          </div>

          <ChoiceComparisonGrid columns={1}>
            {event.choices.map((choice, i) => {
              const immediate =
                choice.cash_impact !== 0
                  ? `${fmtMoney(choice.cash_impact)} cash`
                  : undefined;
              return (
                <LunimarDecisionCard
                  key={i}
                  realm={realm}
                  title={choice.label}
                  immediateEffect={immediate}
                  tradeoff={tradeoffLine(choice)}
                  icon={CATEGORY_ICON[event.category]}
                  risk={impactRisk(choice)}
                  reward={impactReward(choice)}
                  locked={processing}
                  onSelect={() => onChoose(i)}
                />
              );
            })}
          </ChoiceComparisonGrid>
        </div>
      </motion.div>
    </div>
  );
}
